import { create } from "zustand";

/**
 * GlobalStore - Manages global application state
 *
 * Usage:
 * ```tsx
 * import { useGlobalStore } from "@/stores/GlobalStore";
 *
 * const { sidebar } = useGlobalStore();
 * const { isSidebarCompact, setIsSidebarCompact, toggleSidebarCompact } = sidebar;
 * ```
 */
interface GlobalState {
  sidebar: {
    isSidebarCompact: boolean;
    setIsSidebarCompact: (isCompact: boolean) => void;
    toggleSidebarCompact: () => void;
  };
}

const SIDEBAR_COMPACT_STORAGE_KEY = "pathway.sidebar.isCompact";
const SIDEBAR_COMPACT_DEFAULT = true;

const readPersistedSidebarCompact = (): boolean => {
  if (typeof window === "undefined") return SIDEBAR_COMPACT_DEFAULT;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COMPACT_STORAGE_KEY);

    if (raw === null) return SIDEBAR_COMPACT_DEFAULT;

    return raw === "true";
  } catch {
    return SIDEBAR_COMPACT_DEFAULT;
  }
};

const writePersistedSidebarCompact = (isCompact: boolean) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIDEBAR_COMPACT_STORAGE_KEY, String(isCompact));
  } catch {
    // Storage may be unavailable (private mode, quota) — silently degrade
  }
};

export const useGlobalStore = create<GlobalState>((set) => ({
  sidebar: {
    isSidebarCompact: readPersistedSidebarCompact(),
    setIsSidebarCompact: (isCompact: boolean) => {
      writePersistedSidebarCompact(isCompact);
      set((state) => ({ sidebar: { ...state.sidebar, isSidebarCompact: isCompact } }));
    },
    toggleSidebarCompact: () =>
      set((state) => {
        const next = !state.sidebar.isSidebarCompact;

        writePersistedSidebarCompact(next);

        return { sidebar: { ...state.sidebar, isSidebarCompact: next } };
      }),
  },
}));
